/**
 * Per-type / per-tail variable hourly DOC for assignment ranking.
 *
 * hourlyDoc is fuel planning + Mx + engine + optional insurance. Each
 * component inherits tail, then type, then unset. A zero component adds
 * nothing. Trip fees (landing, handling, parking, catering, hotel) and
 * fixed monthly costs are not in this number.
 *
 * Fuel planning ranks and ballparks only. Measured fuel still posts on its
 * own precedence. Mx and engine, when set, override the company reserves on
 * the account ledger. Insurance hourly, when set, posts on the flight-cost
 * ledger. Classic owns those posts — this module only resolves the rates.
 *
 * Rank order among legal tails (soft only — hard rejects stay in canAssign):
 *   1. Customer-requested ICAO type, else company class label
 *   2. City-pair vs practical range, then a requested arrival (or an
 *      explicit "as soon as possible")
 *   3. Lower variable hourly DOC
 *
 * Cruise and range come from the fleet tail. Type rows and tail cost
 * overrides do not replace them. Practical range stays
 * `fleet range × CHARTER_RANGE_FACTOR`.
 *
 * A company-wide reserve is not a stand-in for every type. Known types
 * have different starter rows. A saved type row replaces the seed. A
 * missing row uses the seed when the desk settings object is present.
 */
import { practicalCharterRangeNm } from "./hawker800xp";
import { eteMinutes } from "./generator";
import { hydrateOpsDeskSettings, patchOpsDeskSettings } from "./opsDeskSettings";
import type {
  AircraftSpec,
  CharterRequest,
  OpsAircraftHourlyCost,
  OpsDeskSettings,
  OpsTailHourlyCost,
} from "./types";

export const OPERATING_FIT_WEIGHT = {
  requestType: 16,
  requestClass: 10,
  requestTypeMiss: -18,
  requestClassMiss: -12,
  rangeComfort: 4,
  rangeTight: -4,
  rangeBeyond: -16,
  rangeTightFraction: 0.85,
  arriveOnTime: 8,
  arriveLate: -14,
  earliestNeutralKt: 400,
  earliestStepKt: 25,
  earliestCap: 6,
  costDivisor: 250,
  costCap: 18,
} as const;

/**
 * Editable starter rows. Home-currency planning figures, not a contract
 * and not the only rate the desk will accept.
 *
 * H25B matches the Hawker 800XP desk book (447 KT, NBAA 2470, ~217 USGPH)
 * and the company reserve band (Mx 380, engine 700) so an unedited Hawker
 * posts the same reserves as before.
 * C750 is a Citation X planning seed (not the X+): faster and thirstier
 * than the Hawker, with a higher engine reserve. PC-12 is a turboprop
 * band so it is not priced as a midsize jet.
 * Fuel planning is burn × the 8.50 home-currency / US gal fallback, rounded.
 * It still does not post a fuel line.
 */
export const DOC_TYPE_SEEDS: readonly OpsAircraftHourlyCost[] = [
  {
    icaoType: "H25B",
    classLabel: "midsize",
    fuelPerHour: 1845,
    mxPerHour: 380,
    enginePerHour: 700,
    insurancePerHour: 0,
    cruiseKtas: 447,
    nbaaRangeNm: 2470,
  },
  {
    icaoType: "C750",
    classLabel: "super mid",
    fuelPerHour: 2890,
    mxPerHour: 520,
    enginePerHour: 1100,
    insurancePerHour: 0,
    cruiseKtas: 525,
    nbaaRangeNm: 3070,
  },
  {
    icaoType: "PC12",
    classLabel: "turboprop",
    fuelPerHour: 612,
    mxPerHour: 160,
    enginePerHour: 220,
    insurancePerHour: 0,
    cruiseKtas: 285,
    nbaaRangeNm: 1560,
  },
];

export function docTypeSeed(icaoType: string): OpsAircraftHourlyCost | undefined {
  const token = icaoType.trim().toUpperCase();
  const row = DOC_TYPE_SEEDS.find((item) => item.icaoType === token);
  return row ? { ...row } : undefined;
}

/** Zero row. Empty money and book fields stay unset until the operator fills them. */
export function blankTypeDocRow(icaoType: string): OpsAircraftHourlyCost {
  const token = icaoType.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
  return {
    icaoType: token,
    classLabel: "",
    fuelPerHour: 0,
    mxPerHour: 0,
    enginePerHour: 0,
    insurancePerHour: 0,
    cruiseKtas: 0,
    nbaaRangeNm: 0,
  };
}

/**
 * Saved rows first, then one row per other fleet type. A known type gets its
 * seed. Any other ICAO (FA50 and whatever Fleet adds next) gets a zero row.
 */
export function effectiveAircraftCostTypes(
  ops: CostTables | null | undefined,
  icaoTypes: readonly string[],
): OpsAircraftHourlyCost[] {
  const saved = (ops?.aircraftCosts ?? []).map((row) => ({ ...row }));
  const seen = new Set(saved.map((row) => row.icaoType));
  for (const raw of icaoTypes) {
    const token = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
    if (token.length < 2 || seen.has(token)) continue;
    seen.add(token);
    saved.push(docTypeSeed(token) ?? blankTypeDocRow(token));
  }
  return saved;
}

/** Persist one row per fleet ICAO. An existing saved row, including zeros, is left alone. */
export function ensureTypeDocRows(
  current: OpsDeskSettings | null | undefined,
  icaoTypes: readonly string[],
): OpsDeskSettings {
  const base = hydrateOpsDeskSettings(current);
  const next = effectiveAircraftCostTypes(base, icaoTypes);
  if (
    next.length === base.aircraftCosts.length &&
    next.every((row, index) => row.icaoType === base.aircraftCosts[index]?.icaoType)
  ) {
    return base;
  }
  return patchOpsDeskSettings(base, { aircraftCosts: next });
}

export type OperatingCostSource = "tail" | "type" | "unset";
export type OperatingBookSource = "tail" | "type" | "airframe" | "unset";

export type ResolvedAircraftOperatingCost = {
  aircraftId: string;
  registration: string;
  icaoType: string;
  classLabel: string;
  fuelPerHour: number;
  fuelSource: OperatingCostSource;
  mxPerHour: number;
  mxSource: OperatingCostSource;
  enginePerHour: number;
  engineSource: OperatingCostSource;
  insurancePerHour: number;
  insuranceSource: OperatingCostSource;
  /** Sum of the resolved variable components. Ranking and the Grok snapshot use this. */
  hourlyDoc: number;
  hourlySource: OperatingCostSource;
  cruiseKtas: number;
  cruiseSource: OperatingBookSource;
  nbaaRangeNm: number;
  rangeSource: OperatingBookSource;
  practicalRangeNm: number;
};

export type CustomerAircraftRequest = {
  icaoType: string;
  classLabel: string;
};

export type OperatingFitFactorId = "request_fit" | "range_fit" | "time_fit" | "operating_cost";

export type OperatingFitFactor = {
  id: OperatingFitFactorId;
  delta: number;
  note: string;
};

type CostTables = {
  aircraftCosts?: readonly OpsAircraftHourlyCost[] | null;
  tailCostOverrides?: readonly OpsTailHourlyCost[] | null;
};

function typeRow(ops: CostTables | null | undefined, icaoType: string): OpsAircraftHourlyCost | undefined {
  const token = icaoType.trim().toUpperCase();
  if (!ops) return undefined;
  return ops.aircraftCosts?.find((row) => row.icaoType === token) ?? docTypeSeed(token);
}

function tailRow(ops: CostTables | null | undefined, aircraftId: string): OpsTailHourlyCost | undefined {
  return ops?.tailCostOverrides?.find((row) => row.aircraftId === aircraftId);
}

function airframeBook(airframe: number): { value: number; source: OperatingBookSource } {
  if (airframe > 0) return { value: airframe, source: "airframe" };
  return { value: 0, source: "unset" };
}

function moneyComponent(tail: number, type: number): { value: number; source: OperatingCostSource } {
  if (tail > 0) return { value: tail, source: "tail" };
  if (type > 0) return { value: type, source: "type" };
  return { value: 0, source: "unset" };
}

export function resolveAircraftOperatingCost(
  aircraft: Pick<AircraftSpec, "id" | "registration" | "icaoType" | "cruiseKt" | "rangeNm">,
  ops?: CostTables | null,
): ResolvedAircraftOperatingCost {
  const icaoType = aircraft.icaoType.trim().toUpperCase();
  const type = typeRow(ops, icaoType);
  const tail = tailRow(ops, aircraft.id);
  const fuel = moneyComponent(tail?.fuelPerHour ?? 0, type?.fuelPerHour ?? 0);
  const mx = moneyComponent(tail?.mxPerHour ?? 0, type?.mxPerHour ?? 0);
  const engine = moneyComponent(tail?.enginePerHour ?? 0, type?.enginePerHour ?? 0);
  const insurance = moneyComponent(tail?.insurancePerHour ?? 0, type?.insurancePerHour ?? 0);
  const hourlyDoc = Math.round((fuel.value + mx.value + engine.value + insurance.value) * 100) / 100;
  const hourlySource: OperatingCostSource =
    [fuel, mx, engine, insurance].some((row) => row.source === "tail")
      ? "tail"
      : [fuel, mx, engine, insurance].some((row) => row.source === "type")
        ? "type"
        : "unset";
  const cruise = airframeBook(aircraft.cruiseKt);
  const range = airframeBook(aircraft.rangeNm);
  const practicalRangeNm = range.value > 0 ? practicalCharterRangeNm({ rangeNm: range.value }) : 0;
  return {
    aircraftId: aircraft.id,
    registration: aircraft.registration,
    icaoType,
    classLabel: type?.classLabel ?? "",
    fuelPerHour: fuel.value,
    fuelSource: fuel.source,
    mxPerHour: mx.value,
    mxSource: mx.source,
    enginePerHour: engine.value,
    engineSource: engine.source,
    insurancePerHour: insurance.value,
    insuranceSource: insurance.source,
    hourlyDoc,
    hourlySource,
    cruiseKtas: cruise.value,
    cruiseSource: cruise.source,
    nbaaRangeNm: range.value,
    rangeSource: range.source,
    practicalRangeNm,
  };
}

function classLabelsOf(ops?: CostTables | null): string[] {
  const labels = (ops?.aircraftCosts ?? []).map((row) => row.classLabel.trim()).filter(Boolean);
  return [...new Set(labels)].sort((a, b) => b.length - a.length);
}

function knownTypesOf(ops?: CostTables | null): Set<string> {
  return new Set((ops?.aircraftCosts ?? []).map((row) => row.icaoType.trim().toUpperCase()).filter(Boolean));
}

function matchClassLabel(text: string, labels: readonly string[]): string {
  const lower = text.toLowerCase();
  for (const label of labels) {
    const escaped = label.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    if (new RegExp(`(?:^|[^a-z0-9])${escaped}(?:[^a-z0-9]|$)`, "i").test(lower)) return label;
  }
  return "";
}

function typeTokens(text: string, known: Set<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const match of text.toUpperCase().matchAll(/\b([A-Z][A-Z0-9]{2,3})\b/g)) {
    const token = match[1] ?? "";
    if (seen.has(token)) continue;
    if (!known.has(token) && !/[0-9]/.test(token)) continue;
    seen.add(token);
    out.push(token);
  }
  return out;
}

function askFromText(text: string, ops?: CostTables | null): CustomerAircraftRequest | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const known = knownTypesOf(ops);
  const labels = classLabelsOf(ops);
  const compact = trimmed.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (compact.length >= 3 && compact.length <= 4 && (known.has(compact) || /[0-9]/.test(compact))) {
    return { icaoType: compact, classLabel: "" };
  }
  const tokens = typeTokens(trimmed, known);
  if (tokens.length) {
    const preferred = tokens.find((token) => known.has(token)) ?? tokens[0]!;
    return { icaoType: preferred, classLabel: "" };
  }
  const classLabel = matchClassLabel(trimmed, labels);
  if (classLabel) return { icaoType: "", classLabel };
  return null;
}

/** Customer type or class, when the RFQ actually names one. */
export function resolveCustomerAircraftRequest(
  request: Pick<CharterRequest, "requestedAircraft" | "specialRequests" | "reason" | "emailSubject">,
  ops?: CostTables | null,
): CustomerAircraftRequest {
  const explicit = askFromText(request.requestedAircraft ?? "", ops);
  if (explicit) return explicit;
  const prose = [request.specialRequests, request.reason, request.emailSubject].filter(Boolean).join(" ");
  return askFromText(prose, ops) ?? { icaoType: "", classLabel: "" };
}

function requestHasTechStop(request: Pick<CharterRequest, "itineraryKind" | "techStopIcao" | "proposedLegs">): boolean {
  if (request.itineraryKind === "tech_stop") return true;
  if ((request.techStopIcao ?? "").trim()) return true;
  return (request.proposedLegs ?? []).some((leg) => leg.techStop);
}

function proseOf(request: Pick<CharterRequest, "specialRequests" | "reason" | "emailSubject">): string {
  return [request.specialRequests, request.reason, request.emailSubject].filter(Boolean).join(" ");
}

export function requestedArrivalMs(
  request: Pick<CharterRequest, "arriveByIso" | "specialRequests" | "reason" | "emailSubject">,
  departureMs: number,
): number | null {
  const direct = Date.parse(request.arriveByIso ?? "");
  if (Number.isFinite(direct)) return direct > departureMs ? direct : null;
  const text = proseOf(request);
  const iso = text.match(
    /arrive(?:\s+by|\s+before)?\s+(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:?\d{2})?)/i,
  );
  if (iso?.[1]) {
    const parsed = Date.parse(iso[1].replace(" ", "T"));
    if (Number.isFinite(parsed) && parsed > departureMs) return parsed;
  }
  const clock = text.match(/\barrive(?:\s+by|\s+before)\s+(\d{1,2}):(\d{2})\s*z\b/i);
  if (clock && Number.isFinite(departureMs)) {
    const hour = Number(clock[1]);
    const minute = Number(clock[2]);
    if (hour <= 23 && minute <= 59) {
      const day = new Date(departureMs);
      const utc = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), hour, minute);
      if (utc > departureMs) return utc;
    }
  }
  return null;
}

function wantsEarliest(request: Pick<CharterRequest, "specialRequests" | "reason" | "emailSubject">): boolean {
  return /\b(asap|urgent|earliest|as soon as possible)\b/i.test(proseOf(request));
}

function costDelta(hourly: number, plannedHours: number): number {
  if (hourly <= 0) return 0;
  const basis = plannedHours > 0 ? hourly * plannedHours : hourly;
  return -Math.min(OPERATING_FIT_WEIGHT.costCap, Math.max(1, Math.round(basis / OPERATING_FIT_WEIGHT.costDivisor)));
}

export function scoreAircraftOperatingFit(input: {
  request: Pick<
    CharterRequest,
    | "requestedAircraft"
    | "specialRequests"
    | "reason"
    | "emailSubject"
    | "arriveByIso"
    | "itineraryKind"
    | "techStopIcao"
    | "proposedLegs"
  >;
  aircraft: Pick<AircraftSpec, "id" | "registration" | "icaoType" | "cruiseKt" | "rangeNm">;
  ops?: CostTables | null;
  maxHopNm: number;
  tripNm: number;
  departureMs: number;
  repositionEteMin: number;
}): { factors: OperatingFitFactor[]; cost: OperatingFitFactor | null } {
  const resolved = resolveAircraftOperatingCost(input.aircraft, input.ops);
  const factors: OperatingFitFactor[] = [];
  const ask = resolveCustomerAircraftRequest(input.request, input.ops);
  if (ask.icaoType) {
    const match = resolved.icaoType === ask.icaoType;
    factors.push({
      id: "request_fit",
      delta: match ? OPERATING_FIT_WEIGHT.requestType : OPERATING_FIT_WEIGHT.requestTypeMiss,
      note: match
        ? `Customer asked for ${ask.icaoType} — this tail matches.`
        : `Customer asked for ${ask.icaoType} — this tail is ${resolved.icaoType}.`,
    });
  } else if (ask.classLabel) {
    const match = resolved.classLabel.toLowerCase() === ask.classLabel.toLowerCase();
    factors.push({
      id: "request_fit",
      delta: match ? OPERATING_FIT_WEIGHT.requestClass : OPERATING_FIT_WEIGHT.requestClassMiss,
      note: match
        ? `Customer asked for ${ask.classLabel} — this type is in that class.`
        : `Customer asked for ${ask.classLabel} — this type is not in that class.`,
    });
  }

  const practical = resolved.practicalRangeNm;
  if (input.maxHopNm > 0 && practical > 0 && !requestHasTechStop(input.request)) {
    const hop = Math.round(input.maxHopNm);
    const limit = Math.round(practical);
    if (input.maxHopNm > practical) {
      factors.push({
        id: "range_fit",
        delta: OPERATING_FIT_WEIGHT.rangeBeyond,
        note: `City pair ${hop} NM is beyond practical range ${limit} NM.`,
      });
    } else if (input.maxHopNm > practical * OPERATING_FIT_WEIGHT.rangeTightFraction) {
      factors.push({
        id: "range_fit",
        delta: OPERATING_FIT_WEIGHT.rangeTight,
        note: `City pair ${hop} NM is tight against practical range ${limit} NM.`,
      });
    } else {
      factors.push({
        id: "range_fit",
        delta: OPERATING_FIT_WEIGHT.rangeComfort,
        note: `City pair ${hop} NM fits practical range ${limit} NM.`,
      });
    }
  }

  const arriveMs = requestedArrivalMs(input.request, input.departureMs);
  const cruise = resolved.cruiseKtas;
  if (arriveMs != null && input.tripNm > 0 && cruise > 0) {
    const tripMin = eteMinutes(input.tripNm, cruise);
    const eta = input.departureMs + (input.repositionEteMin + tripMin) * 60_000;
    const onTime = eta <= arriveMs;
    factors.push({
      id: "time_fit",
      delta: onTime ? OPERATING_FIT_WEIGHT.arriveOnTime : OPERATING_FIT_WEIGHT.arriveLate,
      note: onTime
        ? `ETA at ${cruise} KT meets the requested arrival.`
        : `ETA at ${cruise} KT is after the requested arrival.`,
    });
  } else if (wantsEarliest(input.request) && cruise > 0) {
    const raw = Math.round((cruise - OPERATING_FIT_WEIGHT.earliestNeutralKt) / OPERATING_FIT_WEIGHT.earliestStepKt);
    const delta = Math.max(-OPERATING_FIT_WEIGHT.earliestCap, Math.min(OPERATING_FIT_WEIGHT.earliestCap, raw));
    if (delta !== 0) {
      factors.push({
        id: "time_fit",
        delta,
        note:
          delta > 0
            ? `Passengers want the earliest arrival — ${cruise} KT is quicker than a ${OPERATING_FIT_WEIGHT.earliestNeutralKt} KT cruise.`
            : `Passengers want the earliest arrival — ${cruise} KT is slower than a ${OPERATING_FIT_WEIGHT.earliestNeutralKt} KT cruise.`,
      });
    }
  }

  const plannedHours =
    input.tripNm > 0 && cruise > 0 ? eteMinutes(input.tripNm, cruise) / 60 : 0;
  const costPenalty = costDelta(resolved.hourlyDoc, plannedHours);
  const plannedNote =
    plannedHours > 0 ? ` × ${plannedHours.toFixed(2)} planned h` : "";
  const cost =
    resolved.hourlyDoc > 0
      ? {
          id: "operating_cost" as const,
          delta: costPenalty,
          note: `Variable DOC ${resolved.hourlyDoc}/h${plannedNote} (${resolved.hourlySource}) — planned time only. Lower DOC ranks higher.`,
        }
      : null;

  return { factors, cost };
}

/** Both set variable DOC: lower hourly DOC wins a tied score. Unset does not count as free. */
export function hourlyCostTieBreak(
  a: Pick<AircraftSpec, "id" | "registration" | "icaoType" | "cruiseKt" | "rangeNm">,
  b: Pick<AircraftSpec, "id" | "registration" | "icaoType" | "cruiseKt" | "rangeNm">,
  ops?: CostTables | null,
): number {
  const ah = resolveAircraftOperatingCost(a, ops).hourlyDoc;
  const bh = resolveAircraftOperatingCost(b, ops).hourlyDoc;
  if (ah > 0 && bh > 0 && ah !== bh) return ah - bh;
  return 0;
}
